/**
 * useMessageActions - Hook para ações de mensagens
 * Centraliza operações de edição, exclusão, reações, etc.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  editMessage,
  deleteMessage,
  addReaction,
  removeReaction,
  forwardMessage,
} from '../services/chatService';
import type {
  ConversationMessageResponse,
  EditMessageRequest,
  AddReactionRequest,
  RemoveReactionRequest,
  ForwardMessageRequest,
  ConversationMessageListResponse,
} from '@troia-v3/types';

interface UseMessageActionsOptions {
  conversationId: string;
  onEditSuccess?: (message: ConversationMessageResponse) => void;
  onDeleteSuccess?: (messageId: string) => void;
  onReactionSuccess?: () => void;
  onForwardSuccess?: () => void;
}

export const useMessageActions = (options: UseMessageActionsOptions) => {
  const { conversationId, onEditSuccess, onDeleteSuccess, onReactionSuccess, onForwardSuccess } = options;
  const queryClient = useQueryClient();

  // ============================================================================
  // Edit Message Mutation
  // ============================================================================
  const editMessageMutation = useMutation({
    mutationFn: ({ messageId, data }: { messageId: string; data: EditMessageRequest }) =>
      editMessage(messageId, data),

    onMutate: async ({ messageId, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['messages', { conversationId }] });

      // Snapshot previous value
      const previousMessages = queryClient.getQueryData<ConversationMessageListResponse>([
        'messages',
        { conversationId },
      ]);

      // Optimistically update message
      queryClient.setQueryData<ConversationMessageListResponse>(
        ['messages', { conversationId }],
        (old: any) => {
          if (!old) return old;

          return {
            ...old,
            items: old.items.map((msg: any) => {
              if (msg.id === messageId) {
                return {
                  ...msg,
                  content: data.content,
                  isEdited: true,
                  editedAt: new Date().toISOString(),
                };
              }
              return msg;
            }),
          };
        }
      );

      return { previousMessages };
    },

    onSuccess: (message) => {
      toast.success('Mensagem editada com sucesso');
      onEditSuccess?.(message);
    },

    onError: (error, _variables, context) => {
      // Rollback to previous state
      if (context?.previousMessages) {
        queryClient.setQueryData(['messages', { conversationId }], context.previousMessages);
      }
      toast.error('Erro ao editar mensagem');
      console.error('Edit message error:', error);
    },

    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['messages', { conversationId }] });
    },
  });

  // ============================================================================
  // Delete Message Mutation
  // ============================================================================
  const deleteMessageMutation = useMutation({
    mutationFn: ({ messageId, deleteForEveryone }: { messageId: string; deleteForEveryone?: boolean }) =>
      deleteMessage(messageId, { messageId, ...(deleteForEveryone ? { deleteForEveryone: true } : {}) }),

    onMutate: async ({ messageId }) => {
      // ChatBox usa `useInfiniteMessages`, cuja query key é
      // ['messages', 'infinite', conversationId, additionalFilters]. Precisamos
      // casar com `exact: false` pra pegar qualquer variação de additionalFilters.
      const queryKey = ['messages', 'infinite', conversationId];

      await queryClient.cancelQueries({ queryKey, exact: false });

      // Snapshot de TODAS as queries infinite que batem, pra rollback em caso de erro.
      const previousSnapshots = queryClient.getQueriesData<any>({ queryKey, exact: false });

      // Optimistically remove message de todas as pages do infinite cache.
      queryClient.setQueriesData<any>(
        { queryKey, exact: false },
        (oldData: any) => {
          if (!oldData?.pages) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              items: Array.isArray(page?.items)
                ? page.items.filter((msg: any) => msg.id !== messageId)
                : page?.items,
            })),
          };
        }
      );

      return { previousSnapshots };
    },

    onSuccess: (_, { messageId }) => {
      toast.success('Mensagem excluída com sucesso');
      onDeleteSuccess?.(messageId);
    },

    onError: (error, _variables, context) => {
      // Rollback: restaura cada snapshot na sua query original.
      // Toast de erro é disparado pelo interceptor global (api.ts) com a mensagem real do backend,
      // então não duplicamos aqui.
      if (context?.previousSnapshots) {
        context.previousSnapshots.forEach(([key, data]: [unknown, any]) => {
          queryClient.setQueryData(key as any, data);
        });
      }
      console.error('Delete message error:', error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['messages', 'infinite', conversationId],
        exact: false,
      });
    },
  });

  // ============================================================================
  // Add Reaction Mutation
  // ============================================================================
  const addReactionMutation = useMutation({
    mutationFn: (data: AddReactionRequest) => addReaction(data),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', { conversationId }] });
      onReactionSuccess?.();
    },

    onError: (error) => {
      toast.error('Erro ao adicionar reação');
      console.error('Add reaction error:', error);
    },
  });

  // ============================================================================
  // Remove Reaction Mutation
  // ============================================================================
  const removeReactionMutation = useMutation({
    mutationFn: (data: RemoveReactionRequest) => removeReaction(data),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', { conversationId }] });
      onReactionSuccess?.();
    },

    onError: (error) => {
      toast.error('Erro ao remover reação');
      console.error('Remove reaction error:', error);
    },
  });

  // ============================================================================
  // Forward Message Mutation
  // ============================================================================
  const forwardMessageMutation = useMutation({
    mutationFn: (data: ForwardMessageRequest) => forwardMessage(data),

    onSuccess: () => {
      toast.success('Mensagem encaminhada com sucesso');
      onForwardSuccess?.();
    },

    onError: (error) => {
      toast.error('Erro ao encaminhar mensagem');
      console.error('Forward message error:', error);
    },
  });

  return {
    // Edit
    editMessage: editMessageMutation.mutate,
    editMessageAsync: editMessageMutation.mutateAsync,
    isEditingMessage: editMessageMutation.isPending,

    // Delete
    deleteMessage: deleteMessageMutation.mutate,
    deleteMessageAsync: deleteMessageMutation.mutateAsync,
    isDeletingMessage: deleteMessageMutation.isPending,

    // Reactions
    addReaction: addReactionMutation.mutate,
    addReactionAsync: addReactionMutation.mutateAsync,
    removeReaction: removeReactionMutation.mutate,
    removeReactionAsync: removeReactionMutation.mutateAsync,
    isReactionPending: addReactionMutation.isPending || removeReactionMutation.isPending,

    // Forward
    forwardMessage: forwardMessageMutation.mutate,
    forwardMessageAsync: forwardMessageMutation.mutateAsync,
    isForwardingMessage: forwardMessageMutation.isPending,
  };
};
